import { SparklesIcon } from "@heroicons/react/24/solid";

import Card from "../shared/Card/Card";
import classNames from "../../utils/classNames";
import { IVehicle } from "../../models/Vehicle";

interface VehicleIntelligenceCardProps {
  vehicle: IVehicle;
}

// Colour bands for the 0–100 Wilma Recommendation score. Unknown scores fall
// back to a neutral slate theme so empty analytics don't imply a bad signal.
const indexTheme = (score: number | null | undefined) => {
  if (score === null || score === undefined)
    return {
      ring: "ring-slate-200",
      bg: "bg-slate-50",
      text: "text-slate-600",
      bar: "bg-slate-300",
      label: "Unrated",
    };
  if (score >= 75)
    return {
      ring: "ring-emerald-200",
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      bar: "bg-emerald-500",
      label: "Strong",
    };
  if (score >= 50)
    return {
      ring: "ring-amber-200",
      bg: "bg-amber-50",
      text: "text-amber-700",
      bar: "bg-amber-500",
      label: "Fair",
    };
  return {
    ring: "ring-rose-200",
    bg: "bg-rose-50",
    text: "text-rose-700",
    bar: "bg-rose-500",
    label: "Weak",
  };
};

// Page-width variant of the row-card Wilma Intelligence block. Focuses on the
// recommendation score and the supporting signals we currently capture in
// `IVehicle` — kept deliberately light because the underlying analytics
// surface is still being expanded on the backend.
export default function VehicleIntelligenceCard({
  vehicle,
}: VehicleIntelligenceCardProps) {
  const score = vehicle.aiPartsIndex ?? null;
  const theme = indexTheme(score);
  const scoreDisplay =
    score === null || score === undefined ? "—" : Math.round(score);
  const barWidth =
    score === null || score === undefined
      ? 0
      : Math.max(0, Math.min(100, Math.round(score)));

  const signals: { label: string; value: string }[] = [
    {
      label: "Year",
      value: vehicle.dvlaYearOfManufacture
        ? String(vehicle.dvlaYearOfManufacture)
        : "Unknown",
    },
    {
      label: "Mileage",
      value:
        vehicle.odometerInMiles !== null && vehicle.odometerInMiles !== undefined
          ? `${vehicle.odometerInMiles.toLocaleString("en-GB")} mi`
          : "Unknown",
    },
    {
      label: "Fuel",
      value: vehicle.fuelDescription || vehicle.fuelType || "—",
    },
    {
      label: "Derivative",
      value: vehicle.modelRangeDescription ?? "—",
    },
  ];

  return (
    <Card
      title="Vehicle Intelligence"
      titleClassName="border-b border-gray-100"
      headerRight={
        <span
          className={classNames(
            "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ring-1 ring-inset",
            theme.bg,
            theme.text,
            theme.ring
          )}
        >
          <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" />
          {theme.label}
        </span>
      }
    >
      <div className="flex flex-col gap-5 px-4 py-5 sm:px-6">
        {/* Hero recommendation score */}
        <section
          className={classNames(
            "rounded-xl ring-1 ring-inset px-5 py-4",
            theme.bg,
            theme.ring
          )}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Wilma Recommendation
              </p>
              <p
                className={classNames(
                  "mt-1 text-4xl font-bold leading-none",
                  theme.text
                )}
              >
                {scoreDisplay}
                {score !== null && score !== undefined && (
                  <span className="ml-1 text-lg font-medium text-gray-400">
                    /100
                  </span>
                )}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Combined signal from historic parts sales, listing velocity,
                and valuation fit.
              </p>
            </div>
            <div className="w-full sm:w-64">
              <div
                className="h-2 w-full overflow-hidden rounded-full bg-white/60"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={barWidth}
              >
                <div
                  className={classNames(
                    "h-full rounded-full transition-all",
                    theme.bar
                  )}
                  style={{ width: `${barWidth}%` }}
                />
              </div>
              <div className="mt-1 flex justify-between text-[10px] font-medium uppercase tracking-wide text-gray-400">
                <span>Weak</span>
                <span>Strong</span>
              </div>
            </div>
          </div>
        </section>

        {/* Supporting signals */}
        <section>
          <h3 className="text-sm font-semibold text-gray-700">
            Supporting signals
          </h3>
          <dl className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
            {signals.map((s) => (
              <div
                key={s.label}
                className="rounded-lg bg-white px-3 py-2 ring-1 ring-inset ring-gray-200"
              >
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  {s.label}
                </dt>
                <dd className="mt-0.5 text-sm font-semibold text-gray-900">
                  {s.value}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      </div>
    </Card>
  );
}
